import type { BrokerDocument } from "@dune/types";
import { Button, DataTable } from "@dune/ui";
import React, { useState, useCallback } from "react";
import { getDocumentUrl } from "../../../../../api/queries/files.queries";
import { useGetBrokerDocuments } from "../../../../../api/queries/societies.queries";
import { useDeleteBrokerDocumentMutation } from "../../../../../api/mutations/societies.mutations";
import ConfirmationDialog from "../../../../../component/common/ConfirmationDialog";
import { useRequiredParams } from "../../../../../hooks/useRequiredParams";
import { useDocumentStore } from "../../../../../stores/document.store";
import BrokerUploadDocumentModal from "./BrokerUploadDocumentModal";
import { documentsColumns } from "./documentsColumns";
import { useMe } from "../../../../../api/queries/users.queries";

const DocumentsTab: React.FC = () => {
	const { id } = useRequiredParams<{ id: string }>();
	const brokerId = Number.parseInt(id);
	const { data, isLoading, error } = useGetBrokerDocuments(brokerId);
	const selectDocument = useDocumentStore((state) => state.selectDocument);

	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
		null,
	);
	const [downloadingDocumentId, setDownloadingDocumentId] = useState<
		string | null
	>(null);
	const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
		null,
	);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [documentToDelete, setDocumentToDelete] =
		useState<BrokerDocument | null>(null);

	const deleteDocumentMutation = useDeleteBrokerDocumentMutation(brokerId, {
		onMutate: (documentId) => {
			setDeletingDocumentId(documentId);
		},
		onSettled: () => {
			setDeletingDocumentId(null);
		},
	});

	const handleDocumentClick = useCallback(
		async (doc: BrokerDocument) => {
			const docId = doc.id.toString();
			if (loadingDocumentId === docId) return;

			if (!doc.file?.key || doc.file.key.trim() === "") {
				return;
			}

			if (!doc.file.bucket) {
				return;
			}

			setLoadingDocumentId(docId);

			try {
				const url = await getDocumentUrl({
					bucket: doc.file.bucket,
					key: doc.file.key,
				});

				if (url) {
					selectDocument(url, doc.file.id, doc.file.mimeType, doc.file.name);
				}
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'URL du document:",
					error,
				);
			} finally {
				setLoadingDocumentId(null);
			}
		},
		[loadingDocumentId, selectDocument],
	);

	const documents = data || [];

	const handleDeleteClick = useCallback(
		(documentId: string) => {
			const document = documents.find(
				(doc) => doc.id.toString() === documentId,
			);
			if (document) {
				setDocumentToDelete(document);
				setIsDeleteConfirmationOpen(true);
			}
		},
		[documents],
	);

	const handleConfirmDelete = useCallback(async () => {
		if (documentToDelete) {
			await deleteDocumentMutation.mutateAsync(documentToDelete.id.toString());
			setIsDeleteConfirmationOpen(false);
			setDocumentToDelete(null);
		}
	}, [documentToDelete, deleteDocumentMutation]);

	const { me } = useMe();
	const isDuneUser = me?.role === "DUNE";
	const columns = documentsColumns({
		loadingDocumentId,
		downloadingDocumentId,
		setDownloadingDocumentId,
		onDelete: handleDeleteClick,
		deletingDocumentId,
		isDuneUser,
	});

	if (error) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="text-destructive">
					Erreur lors du chargement des documents.
				</div>
			</div>
		);
	}

	return (
		<div className="h-full">
			<div className="flex justify-between items-center mb-4">
				<h2 className="text-text-main-blue font-semibold">Documents</h2>
				<Button onClick={() => setIsUploadModalOpen(true)}>
					Ajouter un document
				</Button>
			</div>
			<DataTable
				data={documents}
				columns={columns}
				noResultsText="Aucun document trouvé"
				isLoading={isLoading}
				variant="static-clean"
				onRowClick={handleDocumentClick}
			/>
			<BrokerUploadDocumentModal
				open={isUploadModalOpen}
				onOpenChange={setIsUploadModalOpen}
				brokerId={brokerId}
			/>
			<ConfirmationDialog
				open={isDeleteConfirmationOpen}
				onOpenChange={setIsDeleteConfirmationOpen}
				title="Supprimer le document"
				description={`Êtes-vous sûr de vouloir supprimer le document "${documentToDelete?.file.name}" ? Cette action est irréversible.`}
				confirmText="Supprimer"
				cancelText="Annuler"
				variant="danger"
				onConfirm={handleConfirmDelete}
				isLoading={deleteDocumentMutation.isPending}
			/>
		</div>
	);
};

export default DocumentsTab;
